/**
 * GalleryModal.tsx - GalleryModal - Full-screen modal for viewing/editing items
 * 
 * CHANGE HISTORY:
 * - 251021_0438 [46df53c] danielbeer: feat: complete comprehensive package migration to full standards
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
import React, { useState, useEffect } from 'react';

const GalleryModal = ({
  item,
  getField,
  updateItem,
  fieldMappings,
  onClose,
  onDelete,
  onNext,
  onPrevious
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(getField(item, 'name') || '');
  const [editedDescription, setEditedDescription] = useState(getField(item, 'description') || '');
  
  useEffect(() => {
    setEditedName(getField(item, 'name') || '');
    setEditedDescription(getField(item, 'description') || '');
  }, [item, getField]);
  
  const handleSave = () => {
    const nameField = fieldMappings['name'];
    const descriptionField = fieldMappings['description'];
    
    const updates = {};
    if (nameField) updates[nameField] = editedName;
    if (descriptionField) updates[descriptionField] = editedDescription;
    
    updateItem(item.id, updates);
    setIsEditing(false);
  };
  
  const coverImage = getField(item, 'coverImage') || getField(item, 'url');
  const tags = getField(item, 'tags') || [];
  const color = getField(item, 'color') || 'var(--border-light)';
  
  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onNext();
      if (e.key === 'ArrowLeft') onPrevious();
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onNext, onPrevious]);
  
  return (
    <div className="gallery-modal-overlay" onClick={onClose}>
      <div className="gallery-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="gallery-modal-header">
          <div className="gallery-modal-nav">
            <button onClick={onPrevious} className="btn-modal-nav">←</button>
            <button onClick={onNext} className="btn-modal-nav">→</button>
          </div>
          
          <div className="gallery-modal-actions">
            {isEditing ? (
              <>
                <button onClick={handleSave} className="btn-modal-save">Save</button>
                <button onClick={() => setIsEditing(false)} className="btn-modal-cancel">Cancel</button>
              </>
            ) : (
              <button onClick={() => setIsEditing(true)} className="btn-modal-edit">Edit</button>
            )}
            <button onClick={onDelete} className="btn-modal-delete">Delete</button>
            <button onClick={onClose} className="btn-modal-close">×</button>
          </div>
        </div>
        
        {/* Body */}
        <div className="gallery-modal-body">
          {/* Cover */}
          {coverImage ? (
            <div className="gallery-modal-cover">
              <img src={coverImage} alt={editedName} />
            </div>
          ) : (
            <div className="gallery-modal-cover-color" style={{ backgroundColor: color }} />
          )}
          
          {/* Content */}
          <div className="gallery-modal-info">
            {isEditing ? (
              <>
                <input
                  type="text"
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  className="gallery-modal-title-input"
                  placeholder="Title..."
                />
                <textarea
                  value={editedDescription}
                  onChange={(e) => setEditedDescription(e.target.value)}
                  className="gallery-modal-description-input"
                  placeholder="Description..."
                  rows={6}
                />
              </>
            ) : (
              <>
                <h2 className="gallery-modal-title">{editedName}</h2>
                <p className="gallery-modal-description">{editedDescription}</p>
              </>
            )}
            
            {tags.length > 0 && (
              <div className="gallery-modal-tags">
                {tags.map((tag, index) => (
                  <span key={index} className="gallery-modal-tag">
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GalleryModal;
